'use client';

import { useLayoutEffect, useRef } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { LeadershipPromotersProps } from '@/types/leadership';
import { getOptimizedImageUrl } from '@/lib/optimized-strapi-image';

/** Split CMS heading at the first colon into two display lines. */
function splitHeadingAtColon(heading: string): { firstLine: string; secondLine: string | null } {
  const trimmed = heading.trim();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex === -1) return { firstLine: trimmed, secondLine: null };

  const firstLine = trimmed.slice(0, colonIndex + 1).trim();
  const secondLine = trimmed.slice(colonIndex + 1).trim();
  return { firstLine, secondLine: secondLine || null };
}

export default function CoreTeamPromoters({ section }: LeadershipPromotersProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const params = useParams();
  const locale = params.locale as string;
  const { firstLine, secondLine } = splitHeadingAtColon(section.heading);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !sectionRef.current) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const headingItems = gsap.utils.toArray<HTMLElement>('[data-promoters-heading]');
      const cards = gsap.utils.toArray<HTMLElement>('[data-promoter-card]');
      const images = gsap.utils.toArray<HTMLElement>('[data-promoter-image]');

      if (!cards.length) {
        return;
      }

      gsap.set(headingItems, { autoAlpha: 0, y: 24 });
      gsap.set(cards, { autoAlpha: 0, y: 56 });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
          toggleActions: 'play none none none',
          once: true,
        },
      });

      if (headingItems.length) {
        timeline.to(headingItems, {
          autoAlpha: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.12,
          ease: 'power3.out',
        });
      }

      timeline.to(
        cards,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          stagger: 0.18,
          ease: 'power3.out',
        },
        '-=0.18',
      );

      images.forEach((image) => {
        gsap.fromTo(
          image,
          { scale: 1.08 },
          {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: image,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1,
            },
          },
        );
      });

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-[#f8f7f4] max-sm:px-4">
      <div className="container mx-auto">
        <div className="w-full">
          <div className="mx-auto text-center">
            <h2
              data-promoters-heading
              className="text-[clamp(1.25rem,4.8vw,3.625rem)] font-semibold leading-[1.15] tracking-tight text-[#111111]"
            >
              <span className="block">{firstLine}</span>
              {secondLine ? <span className="block">{secondLine}</span> : null}
            </h2>
            <div
              data-promoters-heading
              className="prose prose-gray mx-auto mt-3 max-w-2xl text-sm leading-6 text-[#555555] md:text-[15px] [&>p]:mb-0"
              dangerouslySetInnerHTML={{ __html: section.subHeading }}
            />
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-10 sm:mt-12 md:gap-y-12 lg:mt-16 lg:gap-x-12 lg:gap-y-14">
            {section.leadership_teams.map((member) => {
              const imageUrl = getOptimizedImageUrl(member.leaderImage, 'medium');
              const leaderSlug = member.leaderSlug.replace('/leadership/', '');
              const leaderDetailUrl = `/${locale}/leadership/${leaderSlug}`;

              return (
                <article
                  key={member.id}
                  data-promoter-card
                  className="w-full sm:w-[calc((100%-2rem)/2)] lg:w-[calc((100%-6rem)/3)]"
                >
                  <Link href={leaderDetailUrl} className="block group">
                    <div className="relative overflow-hidden rounded-[14px] bg-[#f2f2f2] transition-transform duration-300 group-hover:scale-[1.02]">
                      <div
                        data-promoter-image
                        className="relative aspect-[1.14/1] w-full cursor-pointer will-change-transform sm:aspect-[1.22/0.86]"
                      >
                        <CmsImage
                          src={imageUrl}
                          alt={member.leaderName}
                          fill
                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                          sizes="(max-width: 768px) 100vw, 420px"
                          fallbackSrc={MEDIA_PLACEHOLDER}
                        />
                      </div>
                    </div>

                    <div className="pt-4 sm:pt-5">
                      <h3 className="text-[22px] font-semibold tracking-[-0.02em] text-[#111111] transition-colors duration-200 group-hover:text-[#e34b42] sm:text-[26px] md:text-[32px]">
                        {member.leaderName}
                      </h3>
                      <p className="mt-1 text-sm font-medium text-[#e34b42] md:text-[15px]">
                        {member.designation}
                      </p>
                      <p className="mt-3 max-w-[420px] text-sm leading-6 text-[#333333] md:mt-4 md:text-[15px]">
                        {member.excerpt}
                      </p>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
